import React from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { CheckCircle, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { QuickQuoteForm } from '../components/QuickQuoteForm';

export const RaioXPage = () => {
  const equipments = [
    {
      id: 1,
      name: "RADREX",
      subtitle: "Sistema Digital Versátil",
      description: "Solução digital versátil e econômica, focada em produtividade e conforto. Disponível com estativa tipo Chão-Teto ou Teto, adaptando-se a diferentes espaços.",
      image: "https://static.prod-images.emergentagent.com/jobs/7899ab28-0d6a-4e3b-b0e7-b178b443ae26/images/6dafaf062170f16192879e95fb120e15ee6e9ed52eab5100c20173960d4e5171.png",
      features: [
        "Flexibilidade de instalação (Chão-Teto ou Teto)",
        "Memória de posicionamento com um clique",
        "Mesa Bucky com tampo flutuante 82x210 cm",
        "Ampla cobertura longitudinal e vertical",
        "Ideal para pacientes com mobilidade reduzida"
      ],
      specs: [
        { label: "Mesa", value: "Tampo flutuante 82x210 cm" },
        { label: "Movimentação", value: "Ampla cobertura longitudinal e vertical" },
        { label: "Instalação", value: "Chão-Teto ou Teto" }
      ]
    },
    {
      id: 2,
      name: "VILTIOR",
      subtitle: "Sistema Compacto e Robusto",
      description: "Sistema compacto, robusto e de rápida instalação para otimização de espaços. Desenvolvido para lidar com alta carga de pacientes com facilidade diagnóstica.",
      image: "https://static.prod-images.emergentagent.com/jobs/7899ab28-0d6a-4e3b-b0e7-b178b443ae26/images/6dafaf062170f16192879e95fb120e15ee6e9ed52eab5100c20173960d4e5171.png",
      features: [
        "Alta capacidade para grande volume de pacientes",
        "Interface de usuário comum aos produtos Canon",
        "Gerador de alta frequência para precisão",
        "Detectores wireless Canon ultralight",
        "Instalação rápida e compacta"
      ],
      specs: [
        { label: "Potência", value: "64 kW / 150 kV / 800 mA" },
        { label: "Inversor", value: "Alta frequência" },
        { label: "Capacidade Térmica", value: "300 KHU" },
        { label: "Foco", value: "0,6mm (fino) / 1,2mm (grosso)" }
      ]
    }
  ];

  return (
    <div className="min-h-screen pt-24 pb-20">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-600 via-blue-700 to-cyan-600 py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center space-y-6">
            <h1 className="text-4xl sm:text-5xl font-bold text-white">
              Sistemas de Raio-X
            </h1>
            <p className="text-xl text-blue-100 max-w-3xl mx-auto">
              Soluções digitais versáteis e econômicas para radiografia de alta qualidade
            </p>
          </div>
        </div>
      </section>

      {/* Equipment Cards */}
      <section className="py-16 bg-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="space-y-16">
            {equipments.map((equipment, index) => (
              <div key={equipment.id} className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${index % 2 === 1 ? 'lg:flex-row-reverse' : ''}`}>
                <div className={`${index % 2 === 1 ? 'lg:order-2' : ''}`}>
                  <div className="rounded-2xl overflow-hidden shadow-xl bg-gray-100">
                    <img 
                      src={equipment.image} 
                      alt={equipment.name}
                      className="w-full h-80 object-cover"
                    />
                  </div>
                </div>
                <div className={`space-y-6 ${index % 2 === 1 ? 'lg:order-1' : ''}`}>
                  <div>
                    <span className="text-blue-600 font-semibold text-sm uppercase tracking-wider">
                      {equipment.subtitle}
                    </span>
                    <h2 className="text-3xl font-bold text-gray-900 mt-2">
                      {equipment.name}
                    </h2>
                  </div>
                  <p className="text-lg text-gray-600">
                    {equipment.description}
                  </p>
                  
                  <div className="space-y-3">
                    <h3 className="font-semibold text-gray-900">Características Principais:</h3>
                    <ul className="space-y-2">
                      {equipment.features.map((feature, idx) => (
                        <li key={idx} className="flex items-start space-x-3">
                          <CheckCircle className="text-green-500 flex-shrink-0 mt-0.5" size={20} />
                          <span className="text-gray-600">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <Card className="bg-gray-50">
                    <CardContent className="p-4">
                      <h3 className="font-semibold text-gray-900 mb-3">Especificações Técnicas:</h3>
                      <div className="grid grid-cols-2 gap-3">
                        {equipment.specs.map((spec, idx) => (
                          <div key={idx}>
                            <span className="text-sm text-gray-500">{spec.label}</span>
                            <p className="font-medium text-gray-900">{spec.value}</p>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>

                  <Link to="/contact">
                    <Button className="bg-blue-600 hover:bg-blue-700 text-white">
                      Solicitar Informações
                      <ArrowRight className="ml-2" size={18} />
                    </Button>
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Quick Quote Section */}
      <section className="py-16 bg-gray-50">
        <div className="mx-auto max-w-xl px-4 sm:px-6 lg:px-8">
          <QuickQuoteForm equipmentName="Raio-X" />
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-blue-600">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 text-center">
          <div className="space-y-6">
            <h2 className="text-3xl sm:text-4xl font-bold text-white">
              Precisa de mais informações?
            </h2>
            <p className="text-xl text-blue-100 max-w-2xl mx-auto">
              Nossa equipe está pronta para ajudá-lo a escolher o sistema ideal para sua necessidade
            </p>
            <Link to="/contact">
              <Button size="lg" className="bg-white text-blue-600 hover:bg-gray-100 px-8 py-6 text-lg">
                Fale Conosco
                <ArrowRight className="ml-2" size={20} />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};
